import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import AiChatIcon from '@atlaskit/icon/core/ai-chat';

import { type ConversationAction, HandleActionType } from '@atlassian/rovo-conversation-actions';

export const messages = defineMessages({
	findRelatedPagesMenuItemLabel: {
		id: 'search-page-rovo-config-common.menu.item.label.find-related-pages',
		defaultMessage: 'Find pages related to {query} owned by my team',
		description: 'Label for the Find Related Pages Menu Item',
	},
	summarizeTopResultsMenuItemLabel: {
		id: 'search-page-rovo-config-common.menu.item.label.summarize-top-results',
		defaultMessage: 'Summarize top results about: {query}',
		description: 'Label for the Summarize Top Results Menu Item',
	},
	whoHasRecentlyWorkedMenuItemLabel: {
		id: 'search-page-rovo-config-common.menu.item.label.who-has-recently-worked',
		defaultMessage: 'Who has recently worked on {query} across my org?',
		description: 'Label for the Who Has Recently Worked Menu Item',
	},
	nudgeToQueryStartersTitle: {
		id: 'search-page-rovo-config-common.nudge.title.nudge-to-query-starters',
		defaultMessage: 'Can I help you find something?',
		description: 'Title for the nudge to query starters',
	},
	truncatedFindRelatedPagesMenuItemLabel: {
		id: 'search-page-rovo-config-common.menu.item.label.truncated-find-related-pages',
		defaultMessage: 'Find pages related to my search query that are owned by my team',
		description: 'Truncated label for the Find Related Pages Menu Item',
	},
	truncatedSummarizeTopResultsMenuItemLabel: {
		id: 'search-page-rovo-config-common.menu.item.label.truncated-summarize-top-results',
		defaultMessage: 'Summarize top results about my search query',
		description: 'Truncated label for the Summarize Top Results Menu Item',
	},
	truncatedWhoHasRecentlyWorkedMenuItemLabel: {
		id: 'search-page-rovo-config-common.menu.item.label.truncated-who-has-recently-worked',
		defaultMessage: 'Who has recently worked on items related to my search query across my org?',
		description: 'Truncated label for the Who Has Recently Worked Menu Item',
	},
});

export type GetSearchPageRovoActions = () => {
	id: string;
	action: ConversationAction;
}[];

export const useSearchPageRovoActions = (query: string | undefined): GetSearchPageRovoActions => {
	const { formatMessage } = useIntl();

	const formattedQuery = `'${query}'`;
	const useTruncatedLabels = formattedQuery.length > 52;

	// Find related pages action

	const findRelatedPagesAction = useCallback(
		(): ConversationAction => ({
			id: 'find-related-pages-action',
			title: useTruncatedLabels
				? formatMessage(messages.truncatedFindRelatedPagesMenuItemLabel)
				: formatMessage(messages.findRelatedPagesMenuItemLabel, { query: formattedQuery }),
			icon: () => <AiChatIcon label="" testId="find-related-pages-action.icon" />,
			handleType: HandleActionType.Chat,
			staticPrompt: formatMessage(messages.findRelatedPagesMenuItemLabel, {
				query: formattedQuery,
			}),
			type: 'convo-starter-action',
			starterType: 'static',
		}),
		[formatMessage, formattedQuery, useTruncatedLabels],
	);

	// Summarize top results action

	const summarizeTopResultsAction = useCallback(
		(): ConversationAction => ({
			id: 'summarize-top-results-action',
			title: useTruncatedLabels
				? formatMessage(messages.truncatedSummarizeTopResultsMenuItemLabel)
				: formatMessage(messages.summarizeTopResultsMenuItemLabel, {
						query: formattedQuery,
					}),
			icon: () => <AiChatIcon label="" testId="summarize-top-results-action.icon" />,
			handleType: HandleActionType.Chat,
			staticPrompt: formatMessage(messages.summarizeTopResultsMenuItemLabel, {
				query: formattedQuery,
			}),
			type: 'convo-starter-action',
			starterType: 'static',
		}),
		[formatMessage, formattedQuery, useTruncatedLabels],
	);

	// Who has recently worked action

	const whoHasRecentlyWorkedAction = useCallback(
		(): ConversationAction => ({
			id: 'who-has-recently-worked-action',
			title: useTruncatedLabels
				? formatMessage(messages.truncatedWhoHasRecentlyWorkedMenuItemLabel)
				: formatMessage(messages.whoHasRecentlyWorkedMenuItemLabel, {
						query: formattedQuery,
					}),
			icon: () => <AiChatIcon label="" testId="who-has-recently-worked-action.icon" />,
			handleType: HandleActionType.Chat,
			staticPrompt: formatMessage(messages.whoHasRecentlyWorkedMenuItemLabel, {
				query: formattedQuery,
			}),
			type: 'convo-starter-action',
			starterType: 'static',
		}),
		[formatMessage, formattedQuery, useTruncatedLabels],
	);

	return useCallback(() => {
		if (!query) {
			return [];
		}
		return [
			{ id: 'find-related-pages-action', action: findRelatedPagesAction() },
			{ id: 'summarize-top-results-action', action: summarizeTopResultsAction() },
			{ id: 'who-has-recently-worked-action', action: whoHasRecentlyWorkedAction() },
		];
	}, [findRelatedPagesAction, summarizeTopResultsAction, whoHasRecentlyWorkedAction, query]);
};
